import React from 'react'
import { observer } from 'mobx-react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import AnimatedTabs from '../components/AnimatedTabs'
import ProfileSettings from './ProfileSettings'
import CurrencySettings from './CurrencySettings'
import VerificationSettings from './VerificationSettings'
import TeamSettings from './TeamSettings'
import RolesSettings from './RolesSettings'
import NotificationsSettings from './NotificationsSettings'
import SecuritySettings from './SecuritySettings'
import FeesSettings from './FeesSettings'
import FeaturesSettings from './FeaturesSettings'
import Spinner from '../components/Spinner'
import { RADOM_COLORS } from '../util/Constants'
import { PermissionRequirement, satisfiesPermission, usePermissions } from '../util/permissions'

interface NavItem {
  id: string
  label: string
  permission?: PermissionRequirement
}

interface NavSection {
  group: string
  items: NavItem[]
}

const NAV: NavSection[] = [
  {
    group: 'Organization',
    items: [
      { id: 'profile', label: 'Profile', permission: 'WriteOrganization' },
      { id: 'currency', label: 'Currency', permission: 'WriteOrganization' },
      { id: 'verification', label: 'Verification', permission: 'ReadVerifications' }
    ]
  },
  {
    group: 'Team',
    items: [
      { id: 'members', label: 'Members', permission: 'ReadTeam' },
      { id: 'roles', label: 'Roles', permission: 'ReadRoles' }
    ]
  },
  {
    group: 'User',
    items: [
      { id: 'notifications', label: 'Notifications' },
      { id: 'security', label: 'Security' }
    ]
  },
  {
    group: 'Plan',
    items: [
      { id: 'fees', label: 'Fees' },
      { id: 'features', label: 'Feature Access' }
    ]
  }
]

const ALL_SECTIONS_FLAT = NAV.flatMap(n => n.items)
const LABEL_TO_ID = Object.fromEntries(ALL_SECTIONS_FLAT.map(s => [s.label, s.id]))
const ID_TO_LABEL = Object.fromEntries(ALL_SECTIONS_FLAT.map(s => [s.id, s.label]))
const DEFAULT_SECTION = 'profile'

const SettingsOuter = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: 100%;
`

const Sidebar = styled.nav`
  flex-shrink: 0;
  padding: 32px 0 20px;
  position: sticky;
  top: 66px;
  width: 228px;
  height: calc(100vh - 66px);
  overflow-y: auto;
  background: var(--surface, #ffffff);
  border-right: 1px solid var(--surface-pressed, #edf1f7);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 900px) {
    display: none;
  }
`

const SidebarInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 10px;
`

const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;

  & + & {
    margin-top: 20px;
  }
`

const NavGroupLabel = styled.div`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.055em;
  margin-bottom: 2px;
  padding: 0 11px;
  text-transform: uppercase;
`

const NavItemButton = styled.button<{ $active: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface-pressed, #f3f4f6)' : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? 'var(--border-soft, #eceff3)' : 'transparent'};
  border-radius: 10px;
  color: ${({ $active }) => $active ? 'var(--text, #282828)' : 'var(--text-muted, #566071)'};
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 400;
  gap: 7px;
  min-height: 33px;
  padding: 7px 11px;
  text-align: left;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  width: 100%;

  &:hover {
    background: ${({ $active }) => $active ? 'var(--surface-pressed, #f3f4f6)' : 'var(--surface-hover, #f7f7f8)'};
    color: var(--text, #282828);
  }
`

const MobileTabsWrapper = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: block;
    position: sticky;
    top: 135px;
    background: var(--surface, #ffffff);
    z-index: 3;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
`

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`

const Settings = observer((): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isLoaded } = usePermissions()
  const rawSection = searchParams.get('section') ?? ''

  if (!isLoaded) {
    return <SpinnerWrapper><Spinner /></SpinnerWrapper>
  }

  const nav = NAV
    .map(group => ({ ...group, items: group.items.filter(i => satisfiesPermission(i.permission)) }))
    .filter(group => group.items.length > 0)

  const sections = nav.flatMap(group => group.items)
  const sectionIds = sections.map(i => i.id)

  const activeSection = sectionIds.includes(rawSection)
    ? rawSection
    : sectionIds.includes(DEFAULT_SECTION) ? DEFAULT_SECTION : sectionIds[0]

  const setSection = (id: string): void => {
    setSearchParams({ section: id }, { replace: true })
  }

  return (
    <>
      <MobileTabsWrapper>
        <AnimatedTabs
          selectedTab={ID_TO_LABEL[activeSection]}
          tabs={sections.map(s => s.label)}
          onTabChange={(label) => setSection(LABEL_TO_ID[label])}
        />
      </MobileTabsWrapper>

      <SettingsOuter>
        <Sidebar>

          <SidebarInner>
            {nav.map(group => (
              <NavGroup key={group.group}>
                <NavGroupLabel>{group.group}</NavGroupLabel>
                {group.items.map(item => (
                  <NavItemButton
                    key={item.id}
                    $active={activeSection === item.id}
                    onClick={() => setSection(item.id)}
                  >
                    {item.label}
                  </NavItemButton>
                ))}
              </NavGroup>
            ))}
          </SidebarInner>
        </Sidebar>

        <ContentArea>
          {activeSection === 'profile' && <ProfileSettings />}
          {activeSection === 'currency' && <CurrencySettings />}
          {activeSection === 'verification' && <VerificationSettings />}
          {activeSection === 'members' && <TeamSettings />}
          {activeSection === 'roles' && <RolesSettings />}
          {activeSection === 'notifications' && <NotificationsSettings />}
          {activeSection === 'security' && <SecuritySettings />}
          {activeSection === 'fees' && <FeesSettings />}
          {activeSection === 'features' && <FeaturesSettings />}
        </ContentArea>
      </SettingsOuter>
    </>
  )
})

export default Settings
